import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight, faBuilding, faCircleCheck, faShieldHalved, faUser,
} from '@fortawesome/free-solid-svg-icons';
import quotationsAxe from '../../../../services/axios-client/quotationsAxeClient';
import { getQuotationTypeName, professionalRoles } from '../../../constants';
import CompletedScreenCustomerLinkButton from '../../elements/CompletedScreenCustomerLinkButton';
import {
  DetailGrid,
  DetailItem,
  InfoCard,
  InfoCardHeader,
  InfoCardTitle,
  PrimaryActionLink,
  StatusPill,
} from './common/InfoBoxV2.styles';
import {
  CompletedGrid, QuoteInfo, QuoteList, QuoteRow, TitleIcon, TitleWrap,
} from './CompletedScreen.styles';

// One "Polizza Emessa" (status 7) panel for every product. What differs per
// product (how the name and address are written, the main quote row, where the
// risk quotes link to, ...) lives in PRODUCTS below; the logic is the one the
// old CompletedScreen<Product> components had.

// ---- customer card ------------------------------------------------------------

const NAME = {
  // "Ragione Sociale" for companies, "Nome e Cognome" for people.
  byGender: (c) => (c.gender === 'G'
    ? { label: 'Ragione Sociale', value: c.business_name }
    : { label: 'Nome e Cognome', value: `${c.name} ${c.surname}` }),
  combined: (c) => ({
    label: 'Nome e Cognome/Ragione Sociale',
    value: c.gender === 'G' ? c.business_name : `${c.name} ${c.surname}`,
  }),
  plain: (c) => ({
    label: 'Nome e Cognome',
    value: c.gender === 'G' ? c.business_name : `${c.name} ${c.surname}`,
  }),
  personOnly: (c) => ({ label: 'Nome e Cognome', value: `${c.name} ${c.surname}` }),
};

const ADDRESS = {
  compact: (c) => `${c.address}, ${c.house_number}, ${c.postal_code}`,
  residencialPlace: (c) => c.residencial_place,
  full: (c) => `${c.address} ${c.house_number}, ${c.postal_code} ${c.city_of_residence.name} `
    + `(${c.city_of_residence.communal_territory.car_plate_symbol})`,
};

// ---- where a risk quote's "Vai Al Preventivo" goes ----------------------------

const ANCILLARI_RISK_SLUGS = {
  tutela_legale: 'tutela-legale',
  infortuni_conducente: 'infortuni-conducente',
  cristalli: 'cristalli',
  collisione: 'collisione',
};

const RISK_SLUG = {
  // the risk product's own slug
  slug: (q) => q.product?.slug,
  commercial: () => 'commercial',
  rcProfessionaleSanita: () => 'rc-professionale-sanita',
  // Ancillari: product 13 holds several quote types, each with its own page
  ancillari: (q) => (q.product?.id === 13 ? ANCILLARI_RISK_SLUGS[q.quote_type] : q.product?.slug),
};

// ---- products -----------------------------------------------------------------

const AUTO = {
  name: 'byGender',
  address: 'compact',
  lookupFromQuote: true,
  main: {
    label: 'Responsabilita Civile',
    slug: (rc) => (rc.product_id === 1 ? 'auto' : 'autocarro'),
    visible: (rc) => [1, 3].includes(rc.product_id),
  },
  risks: 'slug',
};

// Products that share the same customer card ("Nome e Cognome", full address).
const standard = (label, slug, extra = {}) => ({
  name: 'plain',
  address: 'full',
  main: { label, slug },
  risks: 'slug',
  ...extra,
});

const PRODUCTS = {
  auto: AUTO,
  autocarro: AUTO,
  moto: {
    ...AUTO,
    main: { label: 'Responsabilita Civile', slug: 'moto' },
    tutela: true,
  },
  'ancillari-auto': {
    name: 'combined',
    address: 'residencialPlace',
    main: { label: 'Ancillari Auto', slug: 'ancillari-auto' },
    risks: 'ancillari',
  },
  casa: {
    name: 'combined',
    address: 'residencialPlace',
    main: { label: 'Casa', slug: 'casa' },
    risks: 'slug',
  },
  profession: {
    name: 'combined',
    address: 'compact',
    main: { label: 'Responsabilita Civile', slug: 'profession' },
    risks: 'slug',
  },
  'tutela-legale': {
    name: 'byGender',
    address: 'compact',
    main: { label: 'Tutela Legale', slug: 'tutela-legale' },
    tutelaExtras: true,
  },
  travel: {
    name: 'personOnly',
    address: 'compact',
    main: { label: 'Responsabilita Civile', slug: 'travel' },
  },
  commercial: {
    companyCard: true,
    main: { label: 'Responsabilita Civile', slug: 'commercial' },
    risks: 'commercial',
  },
  offline: {
    name: 'plain',
    address: 'full',
    companyRow: true,
    main: {
      label: (rc) => (rc.risk_information && rc.risk_information?.product_name
        ? rc?.risk_information?.product_name
        : rc.product.name),
      slug: 'offline',
    },
  },
  salute: standard('Salute', 'salute', { customerLink: true }),
  malattia: standard('Malattia', 'malattia', { customerLink: true }),
  'rc-famiglia': standard('Rc Famiglia', 'rc-famiglia', { customerLink: true }),
  condominio: standard('Condominio', 'condominio', { customerLink: true }),
  infortunio: standard('Infortunio', 'infortunio', { customerLink: true }),
  'pubblico-impiego': standard('Pubblico Impiego', 'pubblico-impiego', { customerLink: true }),
  'sanitaria-stranieri': standard('Sanitaria per Stranieri', 'sanitaria-stranieri', {
    customerLink: true,
    companyRow: true,
  }),
  'monopattini-elettrici': standard('Monopattini Elettrici', 'monopattini-elettrici', { customerLink: true }),
  'rc-professionale-sanita': standard('Professionale Sanità', 'rc-professionale-sanita', {
    customerLink: true,
    risks: 'rcProfessionaleSanita',
  }),
};

const mainCompanyName = (rc) => (rc.company
  ? rc.company.name
  : rc.preventivas_company?.company_name);

function QuoteItem({
  product, company, slug, quotationId,
}) {
  return (
    <QuoteRow>
      <QuoteInfo>
        <p>
          <b>Prodotto:</b>
          {' '}
          {product}
        </p>
        <p>
          <b>Compagnia:</b>
          {' '}
          {company}
        </p>
      </QuoteInfo>
      {slug && (
        <PrimaryActionLink as={Link} to={`/quotes/purchased/${quotationId}/quotation/${slug}`}>
          Vai Al Preventivo
          <FontAwesomeIcon icon={faArrowRight} />
        </PrimaryActionLink>
      )}
    </QuoteRow>
  );
}

function CompletedScreen({
  product,
  quoteStatus,
  quote,
  insuranceRequest,
  rc_quotation,
  risk_quotations = [],
  tutela_quotation,
}) {
  const navigate = useNavigate();
  const { quoteTypes } = useSelector((store) => store.utilities.quoteTypes);
  const [residenceInfo, setResidenceInfo] = useState(null);

  const config = PRODUCTS[product];
  const { customer } = insuranceRequest;
  const residenceCustomer = config.lookupFromQuote
    ? quote?.insurance_request?.customer ?? customer
    : customer;

  const collectMunicipalities = () => {
    try {
      quotationsAxe.get('/form-data/communes').then((res) => {
        const commune = res.data.data.find(
          (commune) => commune.cadastral_code == residenceCustomer.residence_commune_code,
        );
        setResidenceInfo(commune);
      });
    } catch (err) {
      // Do nothing
    }
  };

  useEffect(() => {
    if (quoteStatus === 7 && !config.companyCard) {
      collectMunicipalities();
    }
  }, [quoteStatus]);

  const { main } = config;
  const mainLabel = typeof main.label === 'function' ? main.label(rc_quotation) : main.label;
  const mainSlug = typeof main.slug === 'function' ? main.slug(rc_quotation) : main.slug;
  const showMain = main.visible ? main.visible(rc_quotation) : true;
  const riskSlug = RISK_SLUG[config.risks];
  const otherRisks = risk_quotations.filter((q) => q.company_id != rc_quotation.company_id);

  const renderCustomerRows = () => {
    if (config.companyCard) {
      const company = insuranceRequest.customer
        ? insuranceRequest.customer
        : insuranceRequest.commercial_company;

      return (
        <>
          <DetailItem className="full">
            <dt>Compagnia</dt>
            <dd>{company.business_name}</dd>
          </DetailItem>
          <DetailItem className="full">
            <dt>Attivita</dt>
            <dd>{insuranceRequest?.commercial_company?.activity_description ?? '-'}</dd>
          </DetailItem>
          <DetailItem>
            <dt>Numero Compagnia</dt>
            <dd>{insuranceRequest?.commercial_company?.company_number ?? '-'}</dd>
          </DetailItem>
          <DetailItem>
            <dt>Partita IVA</dt>
            <dd>{company.vat_number}</dd>
          </DetailItem>
          <DetailItem className="full">
            <dt>Indirizzo</dt>
            <dd>{company.vat_number}</dd>
          </DetailItem>
        </>
      );
    }

    const name = NAME[config.name](customer);

    return (
      <>
        {config.companyRow && (
          <DetailItem className="full">
            <dt>Compagnia</dt>
            <dd>{rc_quotation.company.name}</dd>
          </DetailItem>
        )}
        <DetailItem className="full">
          <dt>{name.label}</dt>
          <dd>{name.value}</dd>
        </DetailItem>
        {config.tutelaExtras && customer.plate_number && (
          <DetailItem>
            <dt>Targa Veicolo</dt>
            <dd>{customer.plate_number}</dd>
          </DetailItem>
        )}
        <DetailItem>
          <dt>Email</dt>
          <dd>{customer.email}</dd>
        </DetailItem>
        <DetailItem>
          <dt>Telefono</dt>
          <dd>{customer.phone}</dd>
        </DetailItem>
        <DetailItem className="full">
          <dt>Indirizzo</dt>
          <dd>{ADDRESS[config.address](customer)}</dd>
        </DetailItem>
        <DetailItem>
          <dt>Residenza</dt>
          <dd>{residenceInfo !== null && residenceInfo.name}</dd>
        </DetailItem>
        <DetailItem>
          <dt>Citta</dt>
          <dd>{residenceInfo !== null && residenceInfo.communal_territory.name}</dd>
        </DetailItem>
        {config.tutelaExtras && customer.professional_role && (
          <DetailItem className="full">
            <dt>Ruolo professionale</dt>
            <dd>{professionalRoles.find((role) => role.id === customer.professional_role)?.name}</dd>
          </DetailItem>
        )}
      </>
    );
  };

  return (
    <div>
      {config.customerLink
        && rc_quotation.status === 7
        && rc_quotation.insurance_request.active_customer_id && (
          <CompletedScreenCustomerLinkButton
            onNavigate={() => {
              navigate(`/clients/${rc_quotation.insurance_request.active_customer_id}`);
            }}
            buttonText="Apri dettaglio cliente"
            disabled={false}
          />
      )}
      <CompletedGrid>
        <InfoCard>
          <InfoCardHeader>
            <TitleWrap>
              <TitleIcon>
                <FontAwesomeIcon icon={config.companyCard || customer.gender === 'G' ? faBuilding : faUser} />
              </TitleIcon>
              <InfoCardTitle>Anagrafica Cliente</InfoCardTitle>
            </TitleWrap>
            <StatusPill>
              <FontAwesomeIcon icon={faCircleCheck} style={{ marginRight: 6 }} />
              Polizza Emessa
            </StatusPill>
          </InfoCardHeader>
          <DetailGrid>{renderCustomerRows()}</DetailGrid>
        </InfoCard>

        <InfoCard>
          <InfoCardHeader>
            <TitleWrap>
              <TitleIcon>
                <FontAwesomeIcon icon={faShieldHalved} />
              </TitleIcon>
              <InfoCardTitle>Preventivi</InfoCardTitle>
            </TitleWrap>
          </InfoCardHeader>
          <QuoteList>
            {showMain && (
              <QuoteItem
                product={mainLabel}
                company={mainCompanyName(rc_quotation)}
                slug={mainSlug}
                quotationId={rc_quotation.id}
              />
            )}
            {riskSlug && otherRisks.map((quote, index) => (
              <QuoteItem
                key={index}
                product={getQuotationTypeName(quote.quote_type, quoteTypes)}
                company={quote.company.name}
                slug={riskSlug(quote)}
                quotationId={quote.id}
              />
            ))}
            {config.tutela && tutela_quotation && tutela_quotation.company_id !== rc_quotation.company_id && (
              <QuoteItem
                product="Tutela Legale"
                company={tutela_quotation.company.name}
                slug="tutela-legale"
                quotationId={tutela_quotation.id}
              />
            )}
          </QuoteList>
        </InfoCard>
      </CompletedGrid>
    </div>
  );
}

export default CompletedScreen;
